import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { supervisorApi } from "@/lib/SupervisorApi";
import { Modal } from "./SupervisorPrimitives";

/**
 * Toggle Device-Trust ON/OFF for a specific tenant.
 *
 * When OFF, the tenant's ``/api/auth/login`` skips the per-device
 * approval flow entirely — every device that provides the right PIN
 * is allowed in, no lockouts, no approval codes needed. Useful for
 * single-tablet shops that don't care about physical terminal locking.
 */
export default function DeviceTrustToggleDialog({ tenant, onClose }) {
  const [enabled, setEnabled] = useState(null); // null while loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supervisorApi.getDeviceTrust(tenant.tenant_code)
      .then((r) => { if (!cancelled) setEnabled(!!r.data.enabled); })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Σφάλμα ανάγνωσης κατάστασης");
      });
    return () => { cancelled = true; };
  }, [tenant.tenant_code]);

  const submit = async (nextValue) => {
    setBusy(true);
    try {
      const r = await supervisorApi.setDeviceTrust(tenant.tenant_code, nextValue);
      setEnabled(!!r.data.enabled);
      toast.success(nextValue ? "Ο έλεγχος συσκευής ενεργοποιήθηκε" : "Ο έλεγχος συσκευής απενεργοποιήθηκε");
      setTimeout(onClose, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Έλεγχος Συσκευών · ${tenant.tenant_code}`} onClose={onClose}>
      {enabled === null ? (
        <div className="py-8 grid place-items-center text-zinc-500">
          <Loader2 className="animate-spin" size={24} />
        </div>
      ) : (
        <div className="space-y-4" data-testid="sup-device-trust-dialog">
          <div className={`rounded-lg border-2 p-4 ${enabled ? "border-emerald-700 bg-emerald-950/30" : "border-amber-700 bg-amber-950/30"}`}>
            <div className={`font-bold text-sm flex items-center gap-2 ${enabled ? "text-emerald-300" : "text-amber-300"}`}>
              {enabled ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
              {enabled ? "Έλεγχος ΕΝΕΡΓΟΣ" : "Έλεγχος ΑΝΕΝΕΡΓΟΣ"}
            </div>
            <div className="text-xs text-zinc-400 mt-2 leading-relaxed">
              {enabled ? (
                <>
                  Κάθε νέα συσκευή του πελάτη ζητά <b>8-ψήφιο κωδικό έγκρισης</b>. Οι
                  συσκευές κλειδώνουν μετά από <b>5 λάθος PIN</b> ή <b>30 ημέρες</b> αδράνειας.
                  Ο πελάτης μπορεί να διαχειριστεί συσκευές από «Ρυθμίσεις → Συσκευές».
                </>
              ) : (
                <>
                  Ο έλεγχος συσκευής είναι <b>απενεργοποιημένος</b>. Κάθε συσκευή που δώσει
                  σωστό PIN μπαίνει άμεσα στο POS. Δεν υπάρχει έλεγχος MAC / UUID, δεν
                  παράγονται κωδικοί έγκρισης, δεν κλειδώνουν συσκευές. Χρήσιμο για
                  απλά καταστήματα με μία μοναδική συσκευή.
                </>
              )}
            </div>
          </div>

          <div className="rounded-lg bg-zinc-800/50 border border-zinc-700 p-3 text-xs text-zinc-400">
            <b className="text-zinc-300">Επίδραση αλλαγής:</b> Οι ήδη εγκεκριμένες συσκευές
            παραμένουν εγκεκριμένες. Αν απενεργοποιήσεις τον έλεγχο, ισχύει άμεσα για κάθε
            επόμενη σύνδεση. Αν τον ενεργοποιήσεις ξανά, νέες συσκευές θα χρειάζονται κωδικό.
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800 -mx-6 px-6">
            <button
              onClick={onClose}
              className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-bold"
              data-testid="sup-device-trust-cancel"
            >
              Κλείσιμο
            </button>
            <button
              onClick={() => submit(!enabled)}
              disabled={busy}
              data-testid={enabled ? "sup-device-trust-disable" : "sup-device-trust-enable"}
              className={`h-10 px-5 rounded-lg text-white font-bold text-sm disabled:opacity-50 ${
                enabled
                  ? "bg-amber-700 hover:bg-amber-600"
                  : "bg-emerald-600 hover:bg-emerald-500"
              }`}
            >
              {busy ? "..." : enabled ? "Απενεργοποίηση Ελέγχου" : "Ενεργοποίηση Ελέγχου"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
